import { arrayOf, bool, func, object, oneOfType, shape, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Import contexts and util modules
import {
  getProcess,
  isBookingProcessAlias,
  ITEM,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { isDigitalProductListingType } from '../../util/soulwise-marketplace/listingType';
import { ensureTransaction } from '../../util/data';
import {
  isPriceTierConflictError,
  isPriceTierSoldOutError,
  isPriceTierUnavailableError,
  isTransactionInitiateListingNotFoundError,
} from '../../util/errors';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { DATE_TYPE_DATE, DATE_TYPE_DATETIME, LINE_ITEM_HOUR, propTypes } from '../../util/types';
import { createSlug } from '../../util/urlHelpers';

// Import shared components
import { H2, NamedLink, OrderBreakdown, Page } from '../../components';

import {
  bookingDatesMaybe,
  getBillingDetails,
  getFormattedTotalPrice,
  getShippingDetailsMaybe,
  getTransactionTypeData,
  hasDefaultPaymentMethod,
  hasPaymentExpired,
  hasTransactionPassedPendingPayment,
  pageDataForCheckoutSpeculate,
  processCheckoutWithPayment,
  recoverCheckoutAfterPriceTierChange,
  setOrderPageInitialValues,
  shouldRestartCheckoutTransaction,
  shouldShowCheckoutConfirm,
  shouldShowCheckoutPaymentForm,
  speculatedQuoteMatchesSelectedTier,
  checkoutConfirmHasQuotedTotal,
  checkoutQuoteForDisplay,
} from './CheckoutPageTransactionHelpers.js';
import { getErrorMessages } from './ErrorMessages';

import CustomTopbar from './CustomTopbar';
import DetailsSideCard from './DetailsSideCard';
import MobileListingImage from './MobileListingImage';
import MobileOrderBreakdown from './MobileOrderBreakdown';
import StripePaymentForm from './StripePaymentForm/StripePaymentForm';

import {
  getItemEventTrackingParams,
  sendTrackingEvent,
  TRACKING_EVENTS,
} from '../../analytics/trackingEvent';
import { ListingOverview } from '../../components/ListingOverview/ListingOverview';
import { getDiscountCodeSearchParam } from '../../util/discountCode';
import {
  createContentSubscription,
  confirmContentSubscription,
  transactionLineItems,
  triggerPostBookingWelcomeMessage,
} from '../../util/api';
import { markExpectWelcomeMessage } from '../../util/expectWelcomeMessage';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  findSubscriptionPlan,
  getLowestSubscriptionPlan,
  getSubscriptionTrackingFields,
  isContentSubscriptionListing,
  planAppliesTrial,
  confirmStripeBillingSecret,
  subscriptionCheckoutErrorMessage,
  subscriptionCreateNeedsClientSecret,
  sumCustomerLineItemAmounts,
} from '../../util/digitalSubscription';
import SubscriptionCheckoutBreakdown from './SubscriptionCheckoutBreakdown';
import SubscriptionCheckoutSection from './SubscriptionCheckoutSection';
import CheckoutPackageRecap from './CheckoutPackageRecap';
import CheckoutTierRecap from './CheckoutTierRecap';
import { storeData } from './CheckoutPageSessionHelpers';
import { useCheckoutPriceTierRace } from './useCheckoutPriceTierRace';
import css from './CheckoutPage.module.css';

const { Money } = sdkTypes;

// Stripe PaymentIntent statuses, where user actions are already completed
// https://stripe.com/docs/payments/payment-intents/status
const STRIPE_PI_USER_ACTIONS_DONE_STATUSES = ['processing', 'requires_capture', 'succeeded'];

// Payment charge options
const ONETIME_PAYMENT = 'ONETIME_PAYMENT';
const PAY_AND_SAVE_FOR_LATER_USE = 'PAY_AND_SAVE_FOR_LATER_USE';
const USE_SAVED_CARD = 'USE_SAVED_CARD';

const paymentFlow = (selectedPaymentMethod, saveAfterOnetimePayment) => {
  // Payment mode could be 'replaceCard', but without explicit saveAfterOnetimePayment flag,
  // we'll handle it as one-time payment
  return selectedPaymentMethod === 'defaultCard'
    ? USE_SAVED_CARD
    : saveAfterOnetimePayment
    ? PAY_AND_SAVE_FOR_LATER_USE
    : ONETIME_PAYMENT;
};

/**
 * Construct orderParams object using pageData from session storage, shipping details, and optional payment params.
 * Note: This is used for both speculate transition and real transition
 *       - Speculate transition is called, when the the component is mounted. It's used to test if the data can go through the API validation
 *       - Real transition is made, when the user submits the StripePaymentForm.
 *
 * @param {Object} pageData data that's saved to session storage.
 * @param {Object} shippingDetails shipping address if applicable.
 * @param {Object} optionalPaymentParams (E.g. paymentMethod or setupPaymentMethodForSaving)
 * @param {Object} config app-wide configs. This contains hosted configs too.
 * @returns orderParams.
 */
const getOrderParams = (pageData, shippingDetails, optionalPaymentParams, config) => {
  const { listingType, unitType } = pageData?.listing?.attributes?.publicData || {};
  const hasBookingDates = !!pageData.orderData?.bookingDates?.bookingStart;
  const quantityFromOrder = pageData.orderData?.quantity;
  const isDigitalListing = isDigitalProductListingType(listingType);
  const quantity =
    isDigitalListing || (unitType === ITEM && !hasBookingDates)
      ? quantityFromOrder != null && quantityFromOrder !== ''
        ? quantityFromOrder
        : 1
      : quantityFromOrder;
  const quantityMaybe = quantity != null && quantity !== '' ? { quantity } : {};
  const deliveryMethod = pageData.orderData?.deliveryMethod;
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
  const tierId = pageData.orderData?.tierId;
  const tierIdMaybe = tierId ? { tierId } : {};

  const listingId = pageData?.listing?.id?.uuid;
  const bookingStart = pageData.orderData?.bookingDates?.bookingStart?.toISOString();
  const bookingEnd = pageData.orderData?.bookingDates?.bookingEnd?.toISOString();
  const protectedBookingDatesMaybe = bookingStart && bookingEnd ? { bookingStart, bookingEnd } : {};

  const userTryOnDiscountCodeMaybe = getDiscountCodeSearchParam(location);

  const protectedDataMaybe = {
    protectedData: {
      listingId,
      ...protectedBookingDatesMaybe,
      ...getTransactionTypeData(listingType, unitType, config),
      ...deliveryMethodMaybe,
      ...shippingDetails,
    },
  };

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = {
    listingId: pageData?.listing?.id,
    ...deliveryMethodMaybe,
    ...quantityMaybe,
    ...bookingDatesMaybe(pageData.orderData?.bookingDates),
    ...tierIdMaybe,
    ...protectedDataMaybe,
    ...optionalPaymentParams,
    ...userTryOnDiscountCodeMaybe,
  };
  return orderParams;
};

const fetchSpeculatedTransactionIfNeeded = (
  orderParams,
  pageData,
  fetchSpeculatedTransaction,
  isClockInSync
) => {
  const pageDataListing = pageData.listing;
  const processName =
    pageData?.transaction?.attributes?.processName ||
    pageDataListing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
  const process = processName ? getProcess(resolveLatestProcessName(processName)) : null;
  const speculatePageData = pageDataForCheckoutSpeculate({
    pageData,
    process,
    isClockInSync,
  });
  const tx = speculatePageData ? speculatePageData.transaction : null;

  // If transaction has passed payment-pending state, speculated tx is not needed.
  const shouldFetchSpeculatedTransaction =
    !!pageData?.listing?.id &&
    !!pageData.orderData &&
    !!process &&
    !hasTransactionPassedPendingPayment(tx, process);

  if (shouldFetchSpeculatedTransaction) {
    const processAlias = pageData.listing.attributes.publicData?.transactionProcessAlias;
    const transactionId = tx ? tx.id : null;
    const isInquiryInPaymentProcess =
      tx?.attributes?.lastTransition === process.transitions.INQUIRE;

    const requestTransition = isInquiryInPaymentProcess
      ? process.transitions.REQUEST_PAYMENT_AFTER_INQUIRY
      : process.transitions.REQUEST_PAYMENT;
    const isPrivileged = process.isPrivileged(requestTransition);

    fetchSpeculatedTransaction(
      orderParams,
      processAlias,
      transactionId,
      requestTransition,
      isPrivileged
    );
  }
};

/**
 * Load initial data for the page
 *
 * Since the data for the checkout is not passed in the URL (there
 * might be lots of options in the future), we must pass in the data
 * some other way. Currently the ListingPage sets the initial data
 * for the CheckoutPage's Redux store.
 *
 * For some cases (e.g. a refresh in the CheckoutPage), the Redux
 * store is empty. To handle that case, we store the received data
 * to window.sessionStorage and read it from there if no props from
 * the store exist.
 *
 * This function also sets of fetching the speculative transaction
 * based on this initial data.
 */
export const loadInitialDataForStripePayments = ({
  pageData,
  fetchSpeculatedTransaction,
  fetchStripeCustomer,
  config,
}) => {
  // Fetch currentUser with stripeCustomer entity
  // Note: since there's need for data loading in "componentWillMount" function,
  //       this is added here instead of loadData static function.
  fetchStripeCustomer();

  // Fetch speculated transaction for showing price in order breakdown
  // NOTE: if unit type is line-item/item, quantity needs to be added.
  // The way to pass it to checkout page is through pageData.orderData
  const shippingDetails = {};
  const optionalPaymentParams = {};
  const orderParams = getOrderParams(pageData, shippingDetails, optionalPaymentParams, config);

  fetchSpeculatedTransactionIfNeeded(orderParams, pageData, fetchSpeculatedTransaction);
};

const handleWalletPaymentFlow = async (values, process, props, stripe, setSubmitting) => {
  const { walletPaymentMethod, walletEvent } = values;
  const {
    config,
    pageData,
    onInitiateOrder,
    onConfirmPayment,
    history,
    routeConfiguration,
    dispatch,
    onSubmitCallback,
    intl,
  } = props;

  // Track whether walletEvent.complete() has been called
  let walletEventCompleted = false;

  try {
    // 1. Get order params
    const shippingDetails = getShippingDetailsMaybe(values.formValues);
    const orderParams = getOrderParams(pageData, shippingDetails, {}, config);
    const storedTx = ensureTransaction(pageData.transaction);
    const processAlias = pageData?.listing?.attributes?.publicData?.transactionProcessAlias;

    const isInquiryInPaymentProcess =
      storedTx?.attributes?.lastTransition === process.transitions.INQUIRE;

    const requestTransition = isInquiryInPaymentProcess
      ? process.transitions.REQUEST_PAYMENT_AFTER_INQUIRY
      : process.transitions.REQUEST_PAYMENT;

    // 2. Initiate order
    const order = await onInitiateOrder(
      orderParams,
      processAlias,
      storedTx.id,
      requestTransition,
      true
    );

    // 3. Get client secret
    const {
      stripePaymentIntentClientSecret,
    } = order.attributes.protectedData.stripePaymentIntents.default;

    // 4. Confirm with wallet's PaymentMethod
    // Using handleActions: false to manually handle 3DS after calling complete()
    const { paymentIntent, error } = await stripe.confirmCardPayment(
      stripePaymentIntentClientSecret,
      { payment_method: walletPaymentMethod.id },
      { handleActions: false }
    );

    if (error) {
      walletEvent.complete('fail');
      walletEventCompleted = true;
      setSubmitting(false);
      return;
    }

    // Per Stripe docs: when using handleActions: false, call complete() immediately
    // after confirmCardPayment succeeds, then handle 3DS manually.
    // See: https://stripe.com/docs/js/payment_request/events/on_paymentmethod
    walletEvent.complete('success');
    walletEventCompleted = true;

    // 5. Handle 3DS if needed (after complete() per Stripe docs)
    if (paymentIntent.status === 'requires_action') {
      const { error: actionError } = await stripe.confirmCardPayment(
        stripePaymentIntentClientSecret
      );
      if (actionError) {
        // 3DS failed - payment was already marked as 'success' in wallet UI,
        // but the actual payment failed. User will see an error on the page.
        console.error('3DS authentication failed:', actionError);
        setSubmitting(false);
        return;
      }
    }

    // 6. Confirm payment transition
    await onConfirmPayment(order.id, process.transitions.CONFIRM_PAYMENT, {});

    // 7. Navigate to success
    const orderId = order.id;
    const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
      id: orderId.uuid,
    });
    const initialValues = {
      initialMessageFailedToTransaction: null,
      savePaymentMethodFailed: false,
    };

    setOrderPageInitialValues(initialValues, routeConfiguration, dispatch);
    onSubmitCallback();
    const itemParams = getItemEventTrackingParams(pageData?.listing, config, intl);
    sendTrackingEvent({
      name: TRACKING_EVENTS.PURCHASE,
      parameters: {
        transaction_id: orderId.uuid,
        currency: itemParams.currency,
        value: itemParams.value,
        items: [itemParams],
      },
    });
    setSubmitting(false);
    history.push(orderDetailsPath);
  } catch (err) {
    console.error('Wallet payment flow error:', err);
    // Only call complete('fail') if we haven't already called complete()
    if (!walletEventCompleted) {
      walletEvent?.complete('fail');
    }
    setSubmitting(false);
  }
};

const handleSubscriptionSubmit = async (
  values,
  props,
  stripe,
  setSubmitting,
  selectedPlanId,
  setSubscriptionError
) => {
  const {
    config,
    intl,
    history,
    routeConfiguration,
    currentUser,
    stripeCustomerFetched,
    dispatch,
    onSubmitCallback,
    pageData,
  } = props;
  const listing = pageData?.listing;
  const {
    card,
    message,
    paymentMethod: selectedPaymentMethod,
    formValues,
    walletPaymentMethod,
    walletEvent,
  } = values;
  const hasDefaultPaymentMethodSaved = hasDefaultPaymentMethod(stripeCustomerFetched, currentUser);
  const savedStripePaymentMethodId = hasDefaultPaymentMethodSaved
    ? currentUser?.stripeCustomer?.defaultPaymentMethod?.attributes?.stripePaymentMethodId
    : null;

  try {
    setSubscriptionError?.(null);
    let paymentMethodId = walletPaymentMethod?.id || null;
    if (!paymentMethodId && selectedPaymentMethod !== 'defaultCard' && stripe && card) {
      const billingDetails = getBillingDetails(formValues, currentUser);
      const created = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: billingDetails,
      });
      if (created.error) {
        throw created.error;
      }
      paymentMethodId = created.paymentMethod.id;
    }
    if (!paymentMethodId && selectedPaymentMethod === 'defaultCard') {
      paymentMethodId = savedStripePaymentMethodId;
    }
    if (!paymentMethodId) {
      throw new Error('A card is required to start this subscription.');
    }

    const discountCode =
      getDiscountCodeSearchParam(typeof window !== 'undefined' ? window.location : {})
        ?.userTryOnDiscountCode ||
      props.appliedDiscount?.code ||
      formValues?.discountCode;

    const result = await createContentSubscription({
      listingId: listing?.id?.uuid,
      planId: selectedPlanId,
      paymentMethodId,
      discountCode,
      initialMessage: message,
    });

    if (result?.clientSecret && stripe) {
      const confirm = await confirmStripeBillingSecret(stripe, result.clientSecret, {
        paymentMethodId,
      });
      if (confirm.error) {
        walletEvent?.complete?.('fail');
        throw confirm.error;
      }
      if (result.stripeSubscriptionId) {
        await confirmContentSubscription({
          stripeSubscriptionId: result.stripeSubscriptionId,
        });
      }
    } else if (subscriptionCreateNeedsClientSecret(result)) {
      throw new Error('Payment confirmation is required to start this subscription.');
    }
    walletEvent?.complete?.('success');

    const transactionId = result?.transactionId;
    const plan = findSubscriptionPlan(listing, selectedPlanId);
    const hasTrial = planAppliesTrial(listing, plan);
    const itemParams = {
      ...getItemEventTrackingParams(listing, config, intl),
      ...getSubscriptionTrackingFields(listing, {
        plan,
        ctaVariant: hasTrial ? 'try_for_free' : 'subscribe',
      }),
      stripe_subscription_id: result?.subscriptionId,
      transaction_id: transactionId,
      value: hasTrial ? 0 : (plan?.amount || 0) / 100,
    };
    sendTrackingEvent({
      name: TRACKING_EVENTS.PURCHASE,
      parameters: itemParams,
    });
    sendTrackingEvent({
      name: hasTrial ? TRACKING_EVENTS.TRIAL_STARTED : TRACKING_EVENTS.SUBSCRIPTION_STARTED,
      parameters: itemParams,
    });

    if (transactionId) {
      setOrderPageInitialValues(
        { initialMessageFailedToTransaction: null, savePaymentMethodFailed: false },
        routeConfiguration,
        dispatch
      );
      onSubmitCallback();
      // Same as confirmPayment: Order Details shows typing + polls until the message appears.
      markExpectWelcomeMessage(transactionId);
      triggerPostBookingWelcomeMessage(transactionId, {
        delayMs: 800,
        retryOnSkip: true,
        retryDelayMs: 5000,
      }).catch(() => {
        // Purchase must not fail if messaging trigger fails.
      });
      history.push(pathByRouteName('OrderDetailsPage', routeConfiguration, { id: transactionId }));
    }
    setSubmitting(false);
  } catch (err) {
    console.error('Subscription checkout failed', err);
    walletEvent?.complete?.('fail');
    const message = subscriptionCheckoutErrorMessage(err);
    setSubscriptionError?.(message ? { message } : { message: 'Subscription checkout failed' });
    setSubmitting(false);
  }
};

const handleSubmit = (
  values,
  process,
  props,
  stripe,
  submitting,
  setSubmitting,
  selectedPlanId,
  setSubscriptionError
) => {
  if (submitting) {
    return;
  }
  setSubmitting(true);

  if (
    props.pageData?.orderData?.tierId &&
    !speculatedQuoteMatchesSelectedTier({
      speculatedTransaction: props.speculatedTransaction,
      selectedTierId: props.pageData.orderData.tierId,
    })
  ) {
    setSubmitting(false);
    return;
  }

  if (isContentSubscriptionListing(props.pageData?.listing)) {
    handleSubscriptionSubmit(
      values,
      props,
      stripe,
      setSubmitting,
      selectedPlanId,
      setSubscriptionError
    );
    return;
  }

  const { walletPaymentMethod } = values;
  if (walletPaymentMethod) {
    handleWalletPaymentFlow(values, process, props, stripe, setSubmitting);
    return;
  }

  const {
    history,
    config,
    intl,
    routeConfiguration,
    speculatedTransaction,
    currentUser,
    stripeCustomerFetched,
    paymentIntent,
    dispatch,
    onInitiateOrder,
    onConfirmCardPayment,
    onConfirmPayment,
    onSendMessage,
    onSavePaymentMethod,
    onSubmitCallback,
    pageData,
    setPageData,
    sessionStorageKey,
  } = props;
  const { card, message, paymentMethod: selectedPaymentMethod, formValues } = values;
  const { saveAfterOnetimePayment: saveAfterOnetimePaymentRaw } = formValues;

  const saveAfterOnetimePayment =
    Array.isArray(saveAfterOnetimePaymentRaw) && saveAfterOnetimePaymentRaw.length > 0;
  const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);
  const hasDefaultPaymentMethodSaved = hasDefaultPaymentMethod(stripeCustomerFetched, currentUser);
  const stripePaymentMethodId = hasDefaultPaymentMethodSaved
    ? currentUser?.stripeCustomer?.defaultPaymentMethod?.attributes?.stripePaymentMethodId
    : null;

  // If paymentIntent status is not waiting user action,
  // confirmCardPayment has been called previously.
  const hasPaymentIntentUserActionsDone =
    paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

  const requestPaymentParams = {
    pageData,
    speculatedTransaction,
    stripe,
    card,
    billingDetails: getBillingDetails(formValues, currentUser),
    message,
    paymentIntent,
    hasPaymentIntentUserActionsDone,
    stripePaymentMethodId,
    process,
    onInitiateOrder,
    onConfirmCardPayment,
    onConfirmPayment,
    onSendMessage,
    onSavePaymentMethod,
    sessionStorageKey,
    stripeCustomer: currentUser?.stripeCustomer,
    isPaymentFlowUseSavedCard: selectedPaymentFlow === USE_SAVED_CARD,
    isPaymentFlowPayAndSaveCard: selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE,
    setPageData,
  };

  const shippingDetails = getShippingDetailsMaybe(formValues);
  // Note: optionalPaymentParams contains Stripe paymentMethod,
  // but that can also be passed on Step 2
  // stripe.confirmCardPayment(stripe, { payment_method: stripePaymentMethodId })
  const optionalPaymentParams =
    selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethodSaved
      ? { paymentMethod: stripePaymentMethodId }
      : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
      ? { setupPaymentMethodForSaving: true }
      : {};

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = getOrderParams(pageData, shippingDetails, optionalPaymentParams, config);

  // There are multiple XHR calls that needs to be made against Stripe API and Sharetribe Marketplace API on checkout with payments
  processCheckoutWithPayment(orderParams, requestPaymentParams)
    .then(response => {
      const { orderId, messageSuccess, paymentMethodSaved } = response;
      setSubmitting(false);

      const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
      const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
        id: orderId.uuid,
      });
      const initialValues = {
        initialMessageFailedToTransaction,
        savePaymentMethodFailed: !paymentMethodSaved,
      };

      setOrderPageInitialValues(initialValues, routeConfiguration, dispatch);
      onSubmitCallback();
      const itemParams = getItemEventTrackingParams(pageData?.listing, config, intl);
      sendTrackingEvent({
        name: TRACKING_EVENTS.PURCHASE,
        parameters: {
          transaction_id: orderId.uuid,
          currency: itemParams.currency,
          value: itemParams.value,
          items: [itemParams],
        },
      });
      history.push(orderDetailsPath);
    })
    .catch(err => {
      console.error(err);
      setSubmitting(false);
    });
};

const onStripeInitialized = (stripe, process, props) => {
  const { paymentIntent, onRetrievePaymentIntent, pageData } = props;
  const tx = pageData?.transaction || null;

  // We need to get up to date PI, if payment is pending but it's not expired.
  const shouldFetchPaymentIntent =
    stripe &&
    !paymentIntent &&
    tx?.id &&
    process?.getState(tx) === process?.states.PENDING_PAYMENT &&
    !hasPaymentExpired(tx, process);

  if (shouldFetchPaymentIntent) {
    const { stripePaymentIntentClientSecret } =
      tx.attributes.protectedData?.stripePaymentIntents?.default || {};

    // Fetch up to date PaymentIntent from Stripe
    onRetrievePaymentIntent({ stripe, stripePaymentIntentClientSecret });
  }
};

export const CheckoutPageWithPayment = props => {
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  // Initialized stripe library is saved to state - if it's needed at some point here too.
  const [stripe, setStripe] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [subscriptionLineItems, setSubscriptionLineItems] = useState(null);

  const {
    scrollingDisabled,
    speculateTransactionError,
    speculatedTransaction: speculatedTransactionMaybe,
    isClockInSync,
    initiateOrderError,
    confirmPaymentError,
    intl,
    currentUser,
    confirmCardPaymentError,
    paymentIntent,
    retrievePaymentIntentError,
    stripeCustomerFetched,
    pageData,
    processName,
    listingTitle,
    title,
    config,
    appliedDiscount,
    onValidateDiscount,
    validateDiscountInProgress,
    validateDiscountError,
  } = props;

  // Since the listing data is already given from the ListingPage
  // and stored to handle refreshes, it might not have the possible
  // deleted or closed information in it. If the transaction
  // initiate or the speculative initiate fail due to the listing
  // being deleted or closed, we should dig the information from the
  // errors and not the listing data.
  const listingNotFound =
    isTransactionInitiateListingNotFoundError(speculateTransactionError) ||
    isTransactionInitiateListingNotFoundError(initiateOrderError);

  const { listing, transaction, orderData } = pageData;
  const { setPageData, fetchSpeculatedTransaction, sessionStorageKey } = props;
  const { onClearCheckoutOrderErrors, onRefreshCheckoutListing } = props;
  const {
    soldOutTierIds,
    unitsRefreshNonce,
    hasEligiblePriceTier,
    handleTierAvailabilityChange,
    refreshListingAndUnits,
    soldOutNoticeActive,
    hidePriceTierGoBack,
    hideStaleSoldOutQuote,
    acknowledgeSoldOutNotice,
    holdAutoPick,
    hasPriceTiers,
  } = useCheckoutPriceTierRace({
    listing,
    orderData,
    initiateOrderError,
    speculateTransactionError,
    onRefreshCheckoutListing,
    setPageData,
    pageData,
  });

  const process = processName ? getProcess(resolveLatestProcessName(processName)) : null;
  const handleSelectPriceTier = tierId => {
    acknowledgeSoldOutNotice();
    if (!setPageData) {
      return;
    }
    const recovery = recoverCheckoutAfterPriceTierChange({ pageData, tierId });
    if (!recovery.changed) {
      return;
    }
    if (recovery.shouldClearErrors && onClearCheckoutOrderErrors) {
      onClearCheckoutOrderErrors();
    }
    const nextPageData = recovery.pageData;
    setPageData(nextPageData);
    storeData(nextPageData.orderData, listing, nextPageData.transaction, sessionStorageKey);
    refreshListingAndUnits();
    const orderParams = getOrderParams(nextPageData, {}, {}, config);
    fetchSpeculatedTransactionIfNeeded(
      orderParams,
      nextPageData,
      fetchSpeculatedTransaction,
      isClockInSync
    );
  };
  const isSubscriptionCheckout = isContentSubscriptionListing(listing);
  const subscriptionPlan =
    findSubscriptionPlan(listing, selectedPlanId) || getLowestSubscriptionPlan(listing);

  useEffect(() => {
    if (!isSubscriptionCheckout || !listing?.id || !subscriptionPlan) {
      return undefined;
    }
    let cancelled = false;
    const discountCode =
      appliedDiscount?.code || getDiscountCodeSearchParam(location)?.userTryOnDiscountCode;
    transactionLineItems({
      listingId: listing.id,
      isOwnListing: false,
      orderData: {
        quantity: 1,
        stockReservationQuantity: 1,
        unitPrice: {
          amount: subscriptionPlan.amount,
          currency: subscriptionPlan.currency,
        },
        planId: subscriptionPlan.id,
        ...(discountCode ? { userTryOnDiscountCode: discountCode } : {}),
      },
    })
      .then(response => {
        if (!cancelled) {
          setSubscriptionLineItems(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSubscriptionLineItems(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    isSubscriptionCheckout,
    listing?.id?.uuid,
    subscriptionPlan?.id,
    subscriptionPlan?.amount,
    subscriptionPlan?.currency,
    appliedDiscount?.code,
    location.search,
  ]);
  const existingTransaction = ensureTransaction(transaction);
  const restartCheckout = shouldRestartCheckoutTransaction({
    transaction: existingTransaction,
    selectedTierId: orderData?.tierId,
    process,
    isClockInSync,
  });
  const checkoutTransaction = restartCheckout ? {} : existingTransaction;
  const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);
  const quoteMatchesSelectedTier = speculatedQuoteMatchesSelectedTier({
    speculatedTransaction: speculatedTransactionMaybe,
    selectedTierId: orderData?.tierId,
  });

  // If existing transaction has line-items, it has gone through one of the request-payment transitions.
  // Otherwise, we try to rely on speculatedTransaction for order breakdown data.
  // Never keep a leftover Intro quote after the buyer picked another tier.
  const tx =
    !restartCheckout && existingTransaction?.attributes?.lineItems?.length > 0
      ? existingTransaction
      : quoteMatchesSelectedTier
      ? speculatedTransaction
      : {};
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  const unitType = listing?.attributes?.publicData?.unitType;
  const lineItemUnitType = `line-item/${unitType}`;
  const dateType = lineItemUnitType === LINE_ITEM_HOUR ? DATE_TYPE_DATETIME : DATE_TYPE_DATE;
  const txBookingMaybe = tx?.booking?.id ? { booking: tx.booking, dateType, timeZone } : {};
  const listingType = listing?.attributes?.publicData?.listingType;
  const showTimezoneSelector = !isDigitalProductListingType(listingType);

  // We show discounts to everyone
  // Previously: listing?.attributes?.publicData?.[LISTING_FIELDS.customer_discount]
  const hasDiscountCodeForm = true;

  // Show breakdown only when (speculated?) transaction is loaded
  // (i.e. it has an id and lineItems)
  const breakdown =
    tx.id && tx.attributes.lineItems?.length > 0 ? (
      <OrderBreakdown
        className={css.orderBreakdown}
        userRole="customer"
        transaction={tx}
        {...txBookingMaybe}
        currency={config.currency}
        marketplaceName={config.marketplaceName}
        showTimezoneSelector={showTimezoneSelector}
        listing={listing}
        hasDiscountCodeForm={hasDiscountCodeForm}
        validateDiscountInProgress={props.validateDiscountInProgress}
        validateDiscountError={props.validateDiscountError}
        onValidateDiscount={props.onValidateDiscount}
      />
    ) : null;

  const subscriptionHasTrial =
    isSubscriptionCheckout && planAppliesTrial(listing, subscriptionPlan);
  const subscriptionPayin = isSubscriptionCheckout
    ? sumCustomerLineItemAmounts(subscriptionLineItems) ||
      (subscriptionPlan
        ? { amount: subscriptionPlan.amount, currency: subscriptionPlan.currency }
        : null)
    : null;
  const subscriptionDueToday = subscriptionHasTrial
    ? subscriptionPlan
      ? new Money(0, subscriptionPlan.currency)
      : null
    : subscriptionPayin
    ? new Money(subscriptionPayin.amount, subscriptionPayin.currency)
    : null;
  const renderSubscriptionBreakdown = () =>
    isSubscriptionCheckout && subscriptionPlan ? (
      <SubscriptionCheckoutBreakdown
        listing={listing}
        plan={subscriptionPlan}
        lineItems={subscriptionLineItems}
        intl={intl}
        marketplaceName={config.marketplaceName}
        validateDiscountInProgress={validateDiscountInProgress}
        validateDiscountError={validateDiscountError}
        onValidateDiscount={onValidateDiscount}
      />
    ) : null;

  const rawTotalPrice = isSubscriptionCheckout
    ? subscriptionDueToday
      ? formatMoney(intl, subscriptionDueToday)
      : null
    : tx?.attributes?.lineItems?.length > 0
    ? getFormattedTotalPrice(tx, intl)
    : null;
  const { breakdown: displayBreakdown, totalPrice } = checkoutQuoteForDisplay({
    hideStaleSoldOutQuote: !isSubscriptionCheckout && hideStaleSoldOutQuote,
    breakdown,
    totalPrice: rawTotalPrice,
  });

  const transitions = process.transitions;
  const isPaymentExpired = hasPaymentExpired(checkoutTransaction, process, isClockInSync);

  // Keep the Stripe form mounted after a sold-out 409 or leftover quote so
  // the card iframe is not torn down and remounted (canMakePayment hang).
  // Confirm stays hidden until the new tier's speculate matches.
  const showPaymentForm = isSubscriptionCheckout
    ? !!(currentUser && !listingNotFound)
    : shouldShowCheckoutPaymentForm({
        currentUser,
        listingNotFound,
        initiateOrderError,
        speculateTransactionError,
        retrievePaymentIntentError,
        isPaymentExpired,
      });
  const showConfirmPayment = isSubscriptionCheckout
    ? showPaymentForm
    : shouldShowCheckoutConfirm({
        showPaymentForm,
        quoteMatchesSelectedTier,
        initiateOrderError,
        speculateTransactionError,
        hasEligiblePriceTier: !hasPriceTiers || hasEligiblePriceTier,
        hasQuotedTotal: checkoutConfirmHasQuotedTotal({ hasPriceTiers, totalPrice }),
        soldOutNoticeActive,
      });

  const firstImage = listing?.images?.length > 0 ? listing.images[0] : null;

  const listingLink = (
    <NamedLink
      name="ListingPage"
      params={{ id: listing?.id?.uuid, slug: createSlug(listingTitle) }}
    >
      <FormattedMessage id="CheckoutPage.errorlistingLinkText" />
    </NamedLink>
  );

  const hideConflict = error =>
    hidePriceTierGoBack && (isPriceTierSoldOutError(error) || isPriceTierUnavailableError(error));
  const errorMessages = getErrorMessages(
    listingNotFound,
    hideConflict(initiateOrderError) ? null : initiateOrderError,
    isPaymentExpired,
    retrievePaymentIntentError,
    hideConflict(speculateTransactionError) ? null : speculateTransactionError,
    listingLink
  );

  const txTransitions = existingTransaction?.attributes?.transitions || [];
  const hasInquireTransition = txTransitions.find(tr => tr.transition === transitions.INQUIRE);
  const showInitialMessageInput = !hasInquireTransition;

  // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
  const userName = currentUser?.attributes?.profile
    ? `${currentUser.attributes.profile.firstName} ${currentUser.attributes.profile.lastName}`
    : null;

  // If paymentIntent status is not waiting user action,
  // confirmCardPayment has been called previously.
  const hasPaymentIntentUserActionsDone =
    paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

  // If your marketplace works mostly in one country you can use initial values to select country automatically
  // e.g. {country: 'FI'}

  const initalValuesForStripePayment = { name: userName, recipientName: userName };
  const askShippingDetails =
    orderData?.deliveryMethod === 'shipping' &&
    !hasTransactionPassedPendingPayment(checkoutTransaction, process);

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <CustomTopbar intl={intl} linkToExternalSite={config?.topbar?.logoLink} />
      <div className={css.contentContainer}>
        <div className={css.orderFormContainer}>
          <div className={css.contentMobileContainer}>
            <MobileListingImage
              listingTitle={listingTitle}
              author={listing?.author}
              firstImage={firstImage}
              layoutListingImageConfig={config.layout.listingImage}
            />
            <div className={css.headingContainer}>
              <H2 as="h1" className={css.heading}>
                {title}
              </H2>
              <ListingOverview
                listing={listing}
                author={listing?.author}
                location={location}
                rootClassName={css.ListingOverview}
              />
            </div>

            <MobileOrderBreakdown
              speculateTransactionErrorMessage={errorMessages.speculateTransactionErrorMessage}
              breakdown={isSubscriptionCheckout || hasPriceTiers ? null : displayBreakdown}
            />
          </div>
          <section className={css.paymentContainer}>
            {errorMessages.initiateOrderErrorMessage}
            {errorMessages.listingNotFoundErrorMessage}
            {errorMessages.speculateErrorMessage}
            {errorMessages.retrievePaymentIntentErrorMessage}
            {errorMessages.paymentExpiredMessage}
            {soldOutNoticeActive ? (
              <p className={css.orderError} data-testid="price-tier-sold-out-choose-another">
                <FormattedMessage id="CheckoutPage.priceTierSoldOutChooseAnother" />
              </p>
            ) : null}

            <CheckoutPackageRecap listing={listing} intl={intl} config={config} />
            {hasPriceTiers ? (
              <CheckoutTierRecap
                listing={listing}
                orderData={orderData}
                intl={intl}
                onSelectTier={handleSelectPriceTier}
                onAvailabilityChange={handleTierAvailabilityChange}
                holdAutoPick={holdAutoPick}
                soldOutTierIds={soldOutTierIds}
                unitsRefreshKey={[
                  orderData?.tierId || '',
                  unitsRefreshNonce,
                  isPriceTierConflictError(initiateOrderError) ||
                  isPriceTierConflictError(speculateTransactionError)
                    ? 'sold-out'
                    : 'available',
                ].join(':')}
              />
            ) : null}
            {hasPriceTiers ? (
              <div className={css.subscriptionBreakdownMobile}>{displayBreakdown}</div>
            ) : null}

            {isSubscriptionCheckout ? (
              <>
                <SubscriptionCheckoutSection
                  listing={listing}
                  selectedPlanId={subscriptionPlan?.id}
                  onSelectPlan={setSelectedPlanId}
                  intl={intl}
                />
                <div className={css.subscriptionBreakdownMobile}>
                  {renderSubscriptionBreakdown()}
                </div>
              </>
            ) : null}

            {showPaymentForm ? (
              <StripePaymentForm
                className={css.paymentForm}
                onSubmit={values =>
                  handleSubmit(
                    values,
                    process,
                    props,
                    stripe,
                    submitting,
                    setSubmitting,
                    subscriptionPlan?.id,
                    setSubscriptionError
                  )
                }
                inProgress={submitting}
                formId="CheckoutPagePaymentForm"
                authorDisplayName={listing?.author?.attributes?.profile?.displayName}
                showInitialMessageInput={isSubscriptionCheckout ? false : showInitialMessageInput}
                submitMessageId={
                  subscriptionHasTrial ? 'ProductOrderForm.ctaButtonDigitalTryForFree' : null
                }
                compactPayment={isSubscriptionCheckout}
                initialValues={initalValuesForStripePayment}
                initiateOrderError={
                  subscriptionError ||
                  (isPriceTierConflictError(initiateOrderError) ? null : initiateOrderError)
                }
                confirmCardPaymentError={confirmCardPaymentError}
                confirmPaymentError={confirmPaymentError}
                hasHandledCardPayment={hasPaymentIntentUserActionsDone}
                showSubmitButton={showConfirmPayment}
                loadingData={!stripeCustomerFetched}
                defaultPaymentMethod={
                  hasDefaultPaymentMethod(stripeCustomerFetched, currentUser)
                    ? currentUser.stripeCustomer.defaultPaymentMethod
                    : null
                }
                paymentIntent={paymentIntent}
                onStripeInitialized={stripe => {
                  setStripe(stripe);
                  return onStripeInitialized(stripe, process, props);
                }}
                askShippingDetails={askShippingDetails}
                showPickUplocation={orderData?.deliveryMethod === 'pickup'}
                listingLocation={listing?.attributes?.publicData?.location}
                totalPrice={totalPrice}
                locale={config.localization.locale}
                stripePublishableKey={config.stripe.publishableKey}
                stripeAccountCountry={config.stripe.accountCountry}
                marketplaceName={config.marketplaceName}
                isBooking={isBookingProcessAlias(transactionProcessAlias)}
                isFuzzyLocation={config.maps.fuzzy.enabled}
                listing={listing}
                speculatedTransaction={speculatedTransaction}
                walletPayinTotal={
                  isSubscriptionCheckout && subscriptionDueToday
                    ? {
                        amount: subscriptionDueToday.amount,
                        currency: subscriptionDueToday.currency,
                      }
                    : null
                }
              />
            ) : null}
            {isSubscriptionCheckout ? (
              <p className={css.cancellationRules} data-testid="subscription-cancellation-rules">
                <FormattedMessage id="CheckoutPage.cancellationRules" />
              </p>
            ) : null}
          </section>
        </div>

        <DetailsSideCard
          listing={listing}
          listingTitle={listingTitle}
          author={listing?.author}
          firstImage={firstImage}
          layoutListingImageConfig={config.layout.listingImage}
          speculateTransactionErrorMessage={errorMessages.speculateTransactionErrorMessage}
          isInquiryProcess={false}
          processName={processName}
          breakdown={isSubscriptionCheckout ? renderSubscriptionBreakdown() : displayBreakdown}
          intl={intl}
        />
      </div>
    </Page>
  );
};

CheckoutPageWithPayment.defaultProps = {
  initiateOrderError: null,
  confirmPaymentError: null,
  listing: null,
  orderData: {},
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  currentUser: null,
  paymentIntent: null,
  onValidateDiscount: null,
  onClearCheckoutOrderErrors: null,
  onRefreshCheckoutListing: null,
};

CheckoutPageWithPayment.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  orderData: object,
  fetchStripeCustomer: func.isRequired,
  stripeCustomerFetched: bool.isRequired,
  fetchSpeculatedTransaction: func.isRequired,
  speculateTransactionInProgress: bool.isRequired,
  speculateTransactionError: propTypes.error,
  speculatedTransaction: propTypes.transaction,
  transaction: propTypes.transaction,
  currentUser: propTypes.currentUser,
  params: shape({
    id: string,
    slug: string,
  }).isRequired,
  onConfirmPayment: func.isRequired,
  onInitiateOrder: func.isRequired,
  onConfirmCardPayment: func.isRequired,
  onRetrievePaymentIntent: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onSendMessage: func.isRequired,
  initiateOrderError: propTypes.error,
  confirmPaymentError: propTypes.error,
  // confirmCardPaymentError comes from Stripe so that's why we can't expect it to be in a specific form
  confirmCardPaymentError: oneOfType([propTypes.error, object]),
  paymentIntent: object,
  onValidateDiscount: func,
  onClearCheckoutOrderErrors: func,
  onRefreshCheckoutListing: func,
  appliedDiscount: object,

  // from connect
  dispatch: func.isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

export default CheckoutPageWithPayment;
